import React, { RefObject } from 'react';
import { VideoFeed } from './VideoFeed';
import { CountryFlag } from '../CountryFlag';
import { CountryInfo } from '@/services/geolocation';

type PeerVideoStatus = 'waiting' | 'connecting' | 'reconnecting' | 'connected' | 'disconnected' | 'error';

interface PiPLayoutProps {
  // Video refs
  localVideoRef: RefObject<HTMLVideoElement>;
  remoteVideoRef: RefObject<HTMLVideoElement>;
  canvasRef: RefObject<HTMLCanvasElement>;

  // Streams and media state
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isVideoEnabled: boolean;

  // Peer state
  sessionActive: boolean;
  peerVideoStatus: PeerVideoStatus;
  rtcConnectionState?: string;
  peerVideoEnabled: boolean;
  isPeerVideoHidden: boolean;
  peerAudioEnabled: boolean;

  // Filters
  selfFilter: string;
  peerFilter: string;

  // Layout settings
  swapPositions?: boolean;

  // Audio state
  isPeerAudioMuted: boolean;

  // Country flags
  userCountry?: CountryInfo | null;
  peerCountry?: CountryInfo | null;
}

/**
 * Picture-in-Picture layout component
 * Renders one video as main (fullscreen) and another as small overlay
 */
export const PiPLayout: React.FC<PiPLayoutProps> = ({
  localVideoRef,
  remoteVideoRef,
  canvasRef,
  localStream,
  remoteStream,
  isVideoEnabled,
  sessionActive,
  peerVideoStatus,
  rtcConnectionState,
  peerVideoEnabled,
  isPeerVideoHidden,
  peerAudioEnabled,
  selfFilter,
  peerFilter,
  swapPositions = false,
  isPeerAudioMuted,
  userCountry,
  peerCountry
}) => {
  // Determine which video is main and which is PiP
  const mainIsLocal = swapPositions;
  const pipIsLocal = !swapPositions;

  const isMainViewVisible = mainIsLocal
    ? !!(localStream && isVideoEnabled)
    : !!(sessionActive && peerVideoStatus === 'connected' && peerVideoEnabled);

  const isPipViewVisible = pipIsLocal
    ? !!(localStream && isVideoEnabled)
    : !!(sessionActive && peerVideoStatus === 'connected' && peerVideoEnabled);

  return (
    <div className="relative h-full w-full bg-black overflow-hidden">
      <canvas ref={canvasRef} className="hidden"/>

      {/* Main video (full screen) */}
      <div className="absolute inset-0">
        <VideoFeed
          videoRef={mainIsLocal ? localVideoRef : remoteVideoRef}
          videoType={mainIsLocal ? 'local' : 'remote'}
          stream={mainIsLocal ? localStream : remoteStream}
          filter={mainIsLocal ? selfFilter : peerFilter}
          muted={mainIsLocal ? true : isPeerAudioMuted}
          mirror={mainIsLocal}
          className="absolute inset-0"
          showVideo={mainIsLocal
            ? isMainViewVisible
            : !!(isMainViewVisible && !isPeerVideoHidden)
          }
          overlayState={{
            hasStream: mainIsLocal ? !!localStream : !!remoteStream,
            sessionActive,
            peerVideoStatus,
            isVideoEnabled: mainIsLocal ? isVideoEnabled : undefined,
            isPeerVideoHidden: mainIsLocal ? undefined : isPeerVideoHidden,
            peerVideoEnabled: mainIsLocal ? undefined : peerVideoEnabled,
            rtcConnectionState,
            peerAudioEnabled: mainIsLocal ? undefined : peerAudioEnabled,
            swapPositions,
            userCountry,
            peerCountry
          }}
        />
        {/* Country flag for main video */}
        {!mainIsLocal && sessionActive && rtcConnectionState === 'connected' && peerCountry && (
          <div className="absolute bottom-2 left-2">
            <CountryFlag country={peerCountry} size="sm"/>
          </div>
        )}
      </div>

      {/* Small video (PIP overlay) */}
      <div className="absolute top-4 left-4 w-1/5 aspect-square rounded-lg overflow-hidden border-2 border-slate-700 shadow-lg">
        <VideoFeed
          videoRef={pipIsLocal ? localVideoRef : remoteVideoRef}
          videoType={pipIsLocal ? 'local' : 'remote'}
          stream={pipIsLocal ? localStream : remoteStream}
          filter={pipIsLocal ? selfFilter : peerFilter}
          muted={pipIsLocal ? true : isPeerAudioMuted}
          mirror={pipIsLocal}
          className="absolute inset-0"
          showVideo={pipIsLocal
            ? isPipViewVisible
            : !!(isPipViewVisible && !isPeerVideoHidden)
          }
          overlayState={{
            hasStream: pipIsLocal ? !!localStream : !!remoteStream,
            sessionActive,
            peerVideoStatus,
            isVideoEnabled: pipIsLocal ? isVideoEnabled : undefined,
            isPeerVideoHidden: pipIsLocal ? undefined : isPeerVideoHidden,
            peerVideoEnabled: pipIsLocal ? undefined : peerVideoEnabled,
            rtcConnectionState,
            peerAudioEnabled: pipIsLocal ? undefined : peerAudioEnabled,
            swapPositions,
            userCountry,
            peerCountry
          }}
          overlaySize="small"
        />
        {/* Country flag for PiP video */}
        {!pipIsLocal && sessionActive && rtcConnectionState === 'connected' && peerCountry && (
          <div className="absolute bottom-1 left-1">
            <CountryFlag country={peerCountry} size="sm"/>
          </div>
        )}
      </div>
    </div>
  );
};
